/**
 * Список заявок монтажника: работа и архив.
 *
 * Экран один, режима два. В работе остаются только черновики и данные на
 * проверке — то, к чему монтажник ещё вернётся. Всё, что ушло на подпись,
 * подписано или отклонено, живёт в архиве и не мешается в основном списке.
 *
 * Незавершённую заявку можно открыть и продолжить с того шага, где остановились:
 * если планшет разрядился посреди оформления, ничего не теряется.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api, openContract } from '../api/client'
import type { ApplicationListItem, ApplicationStatus } from '../api/types'
import { Alert, Button, Loading, StatusBadge } from '../components/ui'
import { formatDateTime, statusLabel } from '../lib/format'

interface Props {
  archived: boolean
  onOpen: (applicationId: string) => void
  onCreate: () => void
  onSwitch: () => void
  creating: boolean
  createError: ApiError | null
  onDismissCreateError: () => void
  /** Администратор видит удаление и для зависших договоров (любой статус). */
  isAdmin: boolean
}

/** Статусы, в которых PDF договора уже сформирован и его можно посмотреть. */
const WITH_CONTRACT: ApplicationStatus[] = [
  'pending_signature',
  'signed',
  'ready_for_billing',
  'declined',
]

/**
 * Что можно удалить. Совпадает с DELETABLE_STATUSES на сервере — там правило и
 * проверяется, здесь лишь не показываем заведомо бесполезную кнопку.
 */
const DELETABLE: ApplicationStatus[] = ['draft', 'ocr_done', 'declined']

export function ApplicationsScreen({
  archived,
  isAdmin,
  onOpen,
  onCreate,
  onSwitch,
  creating,
  createError,
  onDismissCreateError,
}: Props) {
  const [items, setItems] = useState<ApplicationListItem[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // Строка поиска в ref, чтобы load() не пересоздавался на каждый символ и не
  // дёргал повторную загрузку через useEffect.
  const searchRef = useRef(search)
  searchRef.current = search

  const load = useCallback(async () => {
    setError(null)
    try {
      setItems(await api.listApplications({ archived, q: searchRef.current }))
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
    }
  }, [archived])

  useEffect(() => {
    // Задержка, чтобы не слать запрос на каждое нажатие: монтажник печатает на
    // планшете, а список перерисовывается целиком.
    const timer = window.setTimeout(() => void load(), search ? 300 : 0)
    return () => window.clearTimeout(timer)
  }, [load, search])

  const showContract = async (item: ApplicationListItem) => {
    setActionError(null)
    setBusyId(item.id)
    try {
      await openContract(item.id)
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Не удалось открыть договор')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (item: ApplicationListItem) => {
    const name = item.contract_number ? `№ ${item.contract_number}` : item.full_name || 'заявку'
    // Для зависших договоров (не draft/ocr_done/declined) удаление — действие
    // администратора: предупреждаем жёстче, чтобы не снести документ случайно.
    const stuck = !DELETABLE.includes(item.status)
    const message = stuck
      ? `Принудительно удалить ${name} (статус «${statusLabel(item.status)}»)? ` +
        'Это действие администратора, договор будет стёрт безвозвратно.'
      : `Удалить ${name}? Данные заявки будут стёрты безвозвратно.`
    if (!window.confirm(message)) {
      return
    }

    setActionError(null)
    setBusyId(item.id)
    try {
      await api.deleteApplication(item.id)
      setItems((prev) => (prev ?? []).filter((row) => row.id !== item.id))
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Не удалось удалить заявку')
    } finally {
      setBusyId(null)
    }
  }

  const searching = search.trim().length > 0

  return (
    <div className="card">
      <h2>{archived ? 'Архив' : 'Мои заявки'}</h2>
      <p className="subtitle">
        {archived
          ? 'Отправленные на подпись, подписанные договоры и отказы абонентов.'
          : 'Черновики и заявки на проверке. Готовые уехали в архив.'}
      </p>

      {createError && !archived && (
        <Alert
          kind="error"
          title="Не удалось создать заявку"
          onRetry={createError.retryable ? onCreate : undefined}
          onDismiss={onDismissCreateError}
        >
          {createError.message}
        </Alert>
      )}

      {archived ? (
        <Button block variant="secondary" onClick={onSwitch}>
          К заявкам в работе
        </Button>
      ) : (
        <Button block onClick={onCreate} busy={creating}>
          Новое подключение
        </Button>
      )}

      <div className="top-actions">
        <Button variant="ghost" onClick={load}>
          Обновить список
        </Button>
        {!archived && (
          <Button variant="ghost" onClick={onSwitch}>
            Архив
          </Button>
        )}
      </div>

      <div className="search">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Поиск: фамилия, адрес, телефон, номер договора"
          aria-label="Поиск заявок"
          autoComplete="off"
        />
        {searching && (
          <button type="button" className="search-clear" onClick={() => setSearch('')}>
            Сбросить
          </button>
        )}
      </div>

      {actionError && (
        <Alert kind="error" title="Действие не выполнено" onDismiss={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {error && (
        <Alert kind="error" title="Список не загрузился" onRetry={load}>
          {error.message}
        </Alert>
      )}

      {!items && !error && <Loading />}

      {items && items.length === 0 && (
        <p className="empty">
          {searching
            ? 'Ничего не найдено. Попробуйте другой запрос.'
            : archived
              ? 'Архив пуст.'
              : 'Заявок в работе нет. Начните с «Нового подключения».'}
        </p>
      )}

      {items && items.length > 0 && (
        <div className="list" style={{ marginTop: 20 }}>
          {items.map((item) => (
            <div key={item.id} className="list-item">
              <button
                type="button"
                className="main"
                onClick={() => onOpen(item.id)}
                aria-label={`Открыть заявку ${item.full_name || 'без имени'}`}
              >
                <div className="name">{item.full_name || 'Без имени'}</div>
                <div className="meta">
                  {item.contract_number ? `№ ${item.contract_number} · ` : ''}
                  {item.connection_address ?? 'адрес не указан'}
                </div>
                <div className="meta">
                  {item.tariff_name ? `${item.tariff_name} · ` : ''}
                  {item.tv_package_name ? `${item.tv_package_name} · ` : ''}
                  изменена {formatDateTime(item.updated_at)}
                </div>
              </button>

              <div className="side">
                <StatusBadge status={item.status} />
                <div className="row-actions">
                  {WITH_CONTRACT.includes(item.status) && (
                    <Button
                      variant="ghost"
                      onClick={() => showContract(item)}
                      busy={busyId === item.id}
                    >
                      Договор
                    </Button>
                  )}
                  {(DELETABLE.includes(item.status) || isAdmin) && (
                    <Button
                      variant="ghost"
                      danger
                      onClick={() => remove(item)}
                      busy={busyId === item.id}
                    >
                      Удалить
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
